import { useState, useEffect } from "react";
import { useStore, CAT_META } from "../store/Store";
import ProductCard from "../components/ProductCard";
import Footer from "../components/Footer";

// Zone-based delivery estimate — same approach real courier partners
// (Delhivery, Bluedart, etc.) and marketplaces use: local city fastest,
// same state next, rest of India slowest. No live API needed — PIN code
// prefixes reliably map to postal zones.
const STORE_CITY_PREFIX = "431"; // Chhatrapati Sambhajinagar (Aurangabad) PIN codes start with this
function estimateDelivery(pincode) {
  if (!/^\d{6}$/.test(pincode)) return null;
  const p3 = parseInt(pincode.slice(0, 3), 10);
  const isSameCity = pincode.startsWith(STORE_CITY_PREFIX);
  const isMaharashtra = pincode[0] === "4" && p3 >= 400 && p3 <= 445 && p3 !== 403; // 403xxx is Goa, not Maharashtra
  if (isSameCity)   return { days: [3, 4], zone: "Chh. Sambhajinagar (local)" };
  if (isMaharashtra) return { days: [5, 7], zone: "Maharashtra" };
  return { days: [6, 9], zone: "Rest of India" };
}

export default function ProductPage({ pid, onNav }) {
  const { dispatch, wish, toggleWish, notify, products, reviews, addReview, addRecentViewed, user, admin, productQA, askQuestion, answerQuestion, adminWhatsApp, freeShipThreshold, shippingFee } = useStore();
  const p = products.find(x=>x.id===pid) || products[0];
  const [sc, setSc] = useState(null);
  const [ss, setSs] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("desc");
  const [pin, setPin] = useState("");
  const [pinMsg, setPinMsg] = useState("");
  const [zoomed, setZoomed] = useState(false);
  const [zoomPos, setZoomPos] = useState({x:50,y:50});
  const [reviewForm, setReviewForm] = useState({name:user?.name||"",rating:5,text:""});
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [answerDrafts, setAnswerDrafts] = useState({});

  const [manualImg, setManualImg] = useState(null);
  useEffect(()=>{ setManualImg(null); }, [sc]); // color change resets to that color's photo (if any)

  useEffect(()=>{ // eslint-disable-next-line
    if(p){ setSc(p.colors?.[0]||"Default"); setSs(p.sizes?.[0]||"Free Size"); addRecentViewed(p.id); } },[p?.id]); // eslint-disable-line

  if(!p) return null;
  const meta  = CAT_META[p.category] || {color:"#EC4899",light:"#FDF2F8",emoji:"👗"};
  const disc  = Math.round((1-p.price/p.mrp)*100);
  const wished = wish.includes(p.id);
  const colors = Array.isArray(p.colors)?p.colors:[p.colors||"Default"];
  const sizes  = Array.isArray(p.sizes)?p.sizes:[p.sizes||"Free Size"];
  const related = products.filter(x=>x.category===p.category&&x.id!==p.id&&x.stock>0).slice(0,6);
  const allReviews = [...(reviews[p.id]||[]), {id:"r1",name:"Priya S.",rating:5,text:"Absolutely love it! Great quality.",date:"2 days ago"},{id:"r2",name:"Sunita R.",rating:4,text:"Fast delivery, exact as described.",date:"1 week ago"},{id:"r3",name:"Meera K.",rating:5,text:"Best purchase! Will buy again.",date:"2 weeks ago"}];

  // Gallery = all uploaded photos for this product (falls back to the single img for older products)
  const gallery = Array.isArray(p.images) && p.images.length ? p.images : (p.img ? [p.img] : []);
  // Priority: a thumbnail the shopper manually clicked > the selected color's own photo (if admin set one) > first gallery photo
  const displayImg = manualImg || (p.colorImages && p.colorImages[sc]) || gallery[0] || p.img;
  const hasSizeStock = p.sizeStock && Object.keys(p.sizeStock).length > 0;
  const currentSizeStock = hasSizeStock ? (p.sizeStock[ss]||0) : p.stock;

  const addCart = () => { dispatch({type:"ADD",p:{...p,sc,ss}}); notify(`${p.name} added to cart!`); };

  const handleZoom = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setZoomPos({x:((e.clientX-r.left)/r.width)*100, y:((e.clientY-r.top)/r.height)*100});
  };

  const submitReview = () => {
    if(!reviewForm.name||!reviewForm.text){notify("Fill name & review","err");return;}
    addReview(p.id,{...reviewForm,date:new Date().toLocaleDateString("en-IN"),id:Date.now()});
    setReviewForm({name:user?.name||"",rating:5,text:""});
    setShowReviewForm(false);
    notify("Review submitted! ⭐");
  };

  const waMsg = encodeURIComponent(
    `🛍️ *Enquiry — Matchwell*\n\n*Product:* ${p.name}\n*Category:* ${p.category}\n*Color:* ${sc}\n*Size:* ${ss}\n*Qty:* ${qty}\n*Price:* ₹${p.price}${displayImg?`\n*Photo:* ${displayImg}`:""}\n\nPlease confirm availability.`
  );

  return (
    <div className="page-top-pad" style={{background:"white",minHeight:"100vh"}}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify({
        "@context": "https://schema.org/",
        "@type": "Product",
        name: p.name,
        image: gallery.length ? gallery : (p.img ? [p.img] : []),
        description: p.desc || p.description || `${p.name} — ${p.category} from Matchwell.`,
        sku: String(p.id),
        brand: { "@type": "Brand", name: "Matchwell" },
        aggregateRating: p.reviews > 0 ? { "@type": "AggregateRating", ratingValue: p.rating || 4.5, reviewCount: p.reviews } : undefined,
        offers: {
          "@type": "Offer",
          url: typeof window!=="undefined" ? window.location.href : "",
          priceCurrency: "INR",
          price: p.price,
          availability: (currentSizeStock>0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        },
      })}}/>
      {/* Breadcrumb */}
      <div style={{padding:"14px clamp(14px,4vw,40px)",borderBottom:"1px solid #f5f5f5"}}>
        <div style={{display:"flex",gap:6,fontSize:12,color:"#999",flexWrap:"wrap"}}>
          {[["Home","home"],["Shop","shop"],[p.category,"shop"]].map(([l,pg],i)=>(
            <span key={i} style={{display:"flex",gap:6,alignItems:"center"}}>
              {i>0&&<span>›</span>}
              <span onClick={()=>onNav(pg,p.category)} style={{cursor:"pointer",color:"#EC4899"}}>{l}</span>
            </span>
          ))}
          <span>›</span><span style={{color:"#1a1a2e",fontWeight:600}}>{p.name}</span>
        </div>
      </div>

      {/* Main grid */}
      <div className="product-detail-grid" style={{display:"grid",gridTemplateColumns:"clamp(280px,44%,540px) 1fr",gap:"clamp(16px,4vw,40px)",padding:"clamp(16px,3vw,32px) clamp(14px,4vw,40px) 40px",maxWidth:1100}}>
        {/* Image */}
        <div>
          <div style={{background:displayImg?"white":`linear-gradient(145deg,${meta.light},white)`,borderRadius:16,height:"clamp(280px,50vw,500px)",display:"flex",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden",cursor:displayImg?"zoom-in":"default"}}
            onMouseMove={displayImg?handleZoom:undefined}
            onMouseEnter={()=>displayImg&&setZoomed(true)}
            onMouseLeave={()=>setZoomed(false)}>
            {displayImg
              ? <img key={displayImg} src={displayImg} alt={`${p.name} - ${sc}`} style={{width:"100%",height:"100%",objectFit:"contain",objectPosition:"center",background:"#fafafa",transition:"transform 0.1s",transform:zoomed?"scale(2)":"scale(1)",transformOrigin:`${zoomPos.x}% ${zoomPos.y}%`}}/>
              : <div style={{textAlign:"center",padding:20}}>
                  <div style={{fontSize:80,opacity:0.15}}>{meta.emoji}</div>
                  <div style={{fontSize:11,color:meta.color,fontWeight:700,letterSpacing:3,textTransform:"uppercase",marginTop:8}}>No Image</div>
                </div>
            }
            {disc>0 && <span style={{position:"absolute",top:14,left:14,background:"#EF4444",color:"white",fontSize:11,fontWeight:800,padding:"4px 12px",borderRadius:20}}>{disc}% OFF</span>}
            {displayImg&&zoomed && <div style={{position:"absolute",bottom:10,right:10,background:"rgba(0,0,0,0.5)",color:"white",fontSize:10,padding:"4px 10px",borderRadius:20}}>🔍 Hover to zoom</div>}
            <button onClick={()=>toggleWish(p.id)} style={{position:"absolute",top:14,right:14,background:"white",border:"none",borderRadius:"50%",width:38,height:38,fontSize:18,cursor:"pointer",boxShadow:"0 2px 12px rgba(0,0,0,0.12)"}}>
              {wished?"❤️":"🤍"}
            </button>
            <button onClick={async ()=>{
              const shareData = { title:p.name, text:`Check out ${p.name} on Matchwell — ₹${p.price}`, url:window.location.href };
              if (navigator.share) { try { await navigator.share(shareData); } catch(e) {} }
              else { navigator.clipboard.writeText(window.location.href); notify("Link copied!"); }
            }} style={{position:"absolute",top:60,right:14,background:"white",border:"none",borderRadius:"50%",width:38,height:38,fontSize:16,cursor:"pointer",boxShadow:"0 2px 12px rgba(0,0,0,0.12)"}}>
              📤
            </button>
          </div>

          {gallery.length > 1 && (
            <div style={{display:"flex",gap:8,marginTop:10,overflowX:"auto",padding:"2px"}}>
              {gallery.map((url,i)=>(
                <button key={i} onClick={()=>setManualImg(url)}
                  style={{flexShrink:0,width:60,height:74,borderRadius:10,overflow:"hidden",border:`2px solid ${displayImg===url?meta.color:"#e5e5e5"}`,padding:0,cursor:"pointer",background:"white"}}>
                  <img src={url} alt={`${p.name} ${i+1}`} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}}
                    onError={e=>e.target.style.opacity=0.15}/>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div style={{fontSize:10,color:meta.color,fontWeight:800,letterSpacing:2,textTransform:"uppercase",marginBottom:6}}>{p.category}</div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(20px,3vw,32px)",fontWeight:900,color:"#1a1a2e",lineHeight:1.2,marginBottom:10}}>{p.name}</h1>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <span style={{color:"#F59E0B",fontSize:14}}>{"★".repeat(Math.floor(p.rating||4.5))}</span>
            <span style={{fontSize:12,color:"#999"}}>{p.rating} · {p.reviews} reviews</span>
            {p.stock>0&&p.stock<=5&&<span style={{fontSize:11,color:"#EF4444",fontWeight:700,background:"#FEF2F2",padding:"2px 8px",borderRadius:20}}>Only {p.stock} left!</span>}
            {p.stock===0&&<span style={{fontSize:11,color:"#EF4444",fontWeight:700}}>Out of Stock</span>}
          </div>
          <div style={{display:"flex",alignItems:"baseline",gap:12,marginBottom:20}}>
            <span style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(26px,4vw,38px)",fontWeight:900,color:"#1a1a2e"}}>₹{p.price?.toLocaleString("en-IN")}</span>
            {p.mrp>p.price&&<span style={{fontSize:15,color:"#aaa",textDecoration:"line-through"}}>₹{p.mrp?.toLocaleString("en-IN")}</span>}
            {disc>0&&<span style={{background:"#EF4444",color:"white",fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:20}}>{disc}% OFF</span>}
          </div>

          {/* Color */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:"#666",marginBottom:8,textTransform:"uppercase",letterSpacing:1}}>Color: <span style={{color:"#1a1a2e"}}>{sc}</span></div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {colors.map(c=>{
                const swatch = p.colorImages && p.colorImages[c];
                return (
                  <button key={c} onClick={()=>setSc(c)} style={{display:"flex",alignItems:"center",gap:6,padding:swatch?"4px 14px 4px 4px":"6px 16px",fontSize:12,border:`2px solid ${sc===c?meta.color:"#e5e5e5"}`,background:sc===c?meta.light:"white",color:sc===c?meta.color:"#666",borderRadius:50,cursor:"pointer",fontWeight:sc===c?700:400}}>
                    {swatch && <img src={swatch} alt={c} style={{width:22,height:22,borderRadius:"50%",objectFit:"cover",border:"1px solid #e5e5e5"}} onError={e=>e.target.style.display="none"}/>}
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Size */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:"#666",marginBottom:8,textTransform:"uppercase",letterSpacing:1,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>Size: <span style={{color:"#1a1a2e"}}>{ss}</span></span>
              <button onClick={()=>setShowSizeChart(!showSizeChart)} style={{fontSize:10,color:meta.color,background:"none",border:`1px solid ${meta.color}`,padding:"3px 10px",borderRadius:20,cursor:"pointer",fontWeight:600}}>📏 Size Chart</button>
            </div>
            {showSizeChart&&(
              <div style={{background:"#fafafa",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${meta.color}33`}}>
                <div style={{fontSize:11,fontWeight:700,color:"#1a1a2e",marginBottom:10}}>📏 Size Guide</div>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                  <thead>
                    <tr style={{background:meta.color,color:"white"}}>
                      {["Size","Chest (in)","Waist (in)","Hip (in)"].map(h=><th key={h} style={{padding:"6px 8px",textAlign:"center",fontWeight:700}}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {[["S","34","28","36"],["M","36","30","38"],["L","38","32","40"],["XL","40","34","42"],["2XL","42","36","44"],["3XL","44","38","46"]].map(([s,c,w,h])=>(
                      <tr key={s} style={{borderBottom:"1px solid #f0f0f0",background:ss===s?meta.light:"white"}}>
                        {[s,c,w,h].map((v,i)=><td key={i} style={{padding:"6px 8px",textAlign:"center",fontWeight:i===0?700:400}}>{v}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{fontSize:10,color:"#999",marginTop:8}}>* Measurements are approximate. When in doubt, size up.</div>
              </div>
            )}
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {sizes.map(sz=>{
                const hasSizeStock = p.sizeStock && Object.keys(p.sizeStock).length > 0;
                const szStock = hasSizeStock ? (p.sizeStock[sz]||0) : p.stock;
                const szOut = szStock <= 0;
                return (
                  <button key={sz} onClick={()=>!szOut && setSs(sz)} disabled={szOut}
                    style={{minWidth:44,padding:"8px 14px",fontSize:12,border:`2px solid ${ss===sz?meta.color:"#e5e5e5"}`,background:szOut?"#f5f5f5":ss===sz?"#1a1a2e":"white",color:szOut?"#ccc":ss===sz?"white":"#666",borderRadius:8,cursor:szOut?"not-allowed":"pointer",fontWeight:700,position:"relative",textDecoration:szOut?"line-through":"none"}}>
                    {sz}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Qty */}
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
            <span style={{fontSize:11,fontWeight:700,color:"#666",textTransform:"uppercase",letterSpacing:1}}>Qty:</span>
            <div style={{display:"flex",alignItems:"center",border:"2px solid #e5e5e5",borderRadius:50}}>
              <button onClick={()=>setQty(q=>Math.max(1,q-1))} style={{width:36,height:36,border:"none",background:"none",cursor:"pointer",fontSize:16,color:"#1a1a2e"}}>−</button>
              <span style={{width:36,textAlign:"center",fontSize:14,fontWeight:700}}>{qty}</span>
              <button onClick={()=>setQty(q=>Math.min(q+1,currentSizeStock))} style={{width:36,height:36,border:"none",background:"none",cursor:"pointer",fontSize:16,color:"#1a1a2e"}}>+</button>
            </div>
            <span style={{fontSize:11,color:"#999"}}>({currentSizeStock} in stock{sizes.length>1?` for size ${ss}`:""})</span>
          </div>

          {/* CTAs */}
          <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
            <button onClick={addCart} disabled={currentSizeStock===0} style={{flex:1,minWidth:130,padding:"13px",background:meta.color,color:"white",border:"none",borderRadius:50,fontSize:13,fontWeight:800,cursor:"pointer",opacity:currentSizeStock===0?0.5:1}}>🛒 Add to Cart</button>
            <button onClick={()=>{addCart();onNav("cart");}} disabled={currentSizeStock===0} style={{flex:1,minWidth:130,padding:"13px",background:"#1a1a2e",color:"white",border:"none",borderRadius:50,fontSize:13,fontWeight:800,cursor:"pointer",opacity:currentSizeStock===0?0.5:1}}>⚡ Buy Now</button>
          </div>

          {/* WhatsApp */}
          <a href={`https://wa.me/${adminWhatsApp}?text=${waMsg}`} target="_blank" rel="noreferrer"
            style={{display:"flex",alignItems:"center",gap:12,background:"#F0FDF4",border:"1px solid #86EFAC",padding:"13px 18px",borderRadius:16,textDecoration:"none",marginBottom:16}}>
            <span style={{fontSize:22}}>💬</span>
            <div>
              <div style={{fontWeight:800,fontSize:13,color:"#16A34A"}}>Enquire / Order via WhatsApp</div>
              <div style={{fontSize:11,color:"#666"}}>Product details sent automatically</div>
            </div>
          </a>

          {/* Pincode */}
          <div style={{background:"#fafafa",padding:14,borderRadius:14,marginBottom:14,border:"1px solid #f0f0f0"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#666",marginBottom:8,textTransform:"uppercase",letterSpacing:1}}>📍 Check Delivery</div>
            <div style={{display:"flex",gap:8}}>
              <input value={pin} onChange={e=>setPin(e.target.value)} maxLength={6} placeholder="Enter Pincode" style={{flex:1,padding:"9px 14px",border:"1px solid #e5e5e5",borderRadius:50,fontSize:13,outline:"none"}}/>
              <button onClick={()=>{
                const est = estimateDelivery(pin);
                if (!est) { setPinMsg("Enter valid 6-digit pincode"); return; }
                const byDate = new Date(Date.now() + est.days[1]*86400000).toLocaleDateString("en-IN",{weekday:"short",month:"short",day:"numeric"});
                setPinMsg(`✓ Delivery in ${est.days[0]}-${est.days[1]} days (${est.zone}) — by ${byDate}`);
              }} style={{padding:"9px 18px",background:"#1a1a2e",color:"white",border:"none",borderRadius:50,fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>Check</button>
            </div>
            {pinMsg&&<div style={{fontSize:12,marginTop:6,color:pinMsg.includes("✓")?"#16A34A":"#EF4444",fontWeight:600}}>{pinMsg}</div>}
            {pinMsg&&pinMsg.includes("✓")&&<div style={{fontSize:10,marginTop:3,color:"#999"}}>Estimate excludes Sundays and public holidays</div>}
          </div>

          {/* Details grid */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {[["🏭","Material",p.material||"—"],["🧺","Care",p.care||"—"],["↩️","Returns","7 days easy"],["🚀","Delivery",freeShipThreshold>0?`Free above ₹${freeShipThreshold.toLocaleString("en-IN")}`:"Free on all orders"]].map(([ic,k,v])=>(
              <div key={k} style={{background:"#fafafa",padding:"10px 14px",borderRadius:12,fontSize:12}}>
                <span style={{marginRight:6}}>{ic}</span><span style={{color:"#999"}}>{k}: </span><span style={{fontWeight:600,color:"#1a1a2e"}}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{padding:"0 clamp(14px,4vw,40px) 40px",maxWidth:900}}>
        <div style={{display:"flex",borderBottom:"2px solid #f0f0f0",marginBottom:20,gap:4,overflowX:"auto"}}>
          {[["desc","Description"],["rev","Reviews"],["qa","Q&A"],["ship","Shipping"]].map(([t,l])=>(
            <button key={t} onClick={()=>setTab(t)} style={{padding:"12px 20px",border:"none",borderBottom:`2px solid ${tab===t?meta.color:"transparent"}`,background:"none",color:tab===t?meta.color:"#999",fontWeight:tab===t?700:400,cursor:"pointer",fontSize:13,marginBottom:-2,whiteSpace:"nowrap",fontFamily:"'DM Sans',sans-serif"}}>
              {l}
            </button>
          ))}
        </div>

        {tab==="desc"&&<p style={{fontSize:14,lineHeight:1.8,color:"#666"}}>{p.desc||"No description."}</p>}

        {tab==="rev"&&(
          <div style={{maxWidth:680}}>
            {/* Rating summary */}
            <div style={{display:"flex",alignItems:"center",gap:20,background:"#fafafa",borderRadius:16,padding:20,marginBottom:16}}>
              <div style={{textAlign:"center",flexShrink:0}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:44,fontWeight:900,color:"#1a1a2e",lineHeight:1}}>{p.rating||4.5}</div>
                <div style={{color:"#F59E0B",fontSize:16,margin:"4px 0"}}>{"★".repeat(Math.floor(p.rating||4.5))}</div>
                <div style={{fontSize:11,color:"#999"}}>{allReviews.length} reviews</div>
              </div>
              <div style={{flex:1}}>
                {[5,4,3,2,1].map(r=>{
                  const cnt=allReviews.filter(x=>(x.rating||x.r)===r).length;
                  const pct=allReviews.length?Math.round((cnt/allReviews.length)*100):0;
                  return (
                    <div key={r} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:11,color:"#999",width:10}}>{r}</span>
                      <span style={{color:"#F59E0B",fontSize:10}}>★</span>
                      <div style={{flex:1,height:6,background:"#f0f0f0",borderRadius:3,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${pct}%`,background:"#F59E0B",borderRadius:3}}/>
                      </div>
                      <span style={{fontSize:10,color:"#999",width:20}}>{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <button onClick={()=>setShowReviewForm(!showReviewForm)} style={{background:meta.color,color:"white",border:"none",padding:"10px 22px",borderRadius:50,fontSize:12,fontWeight:700,cursor:"pointer",marginBottom:16}}>✍️ Write a Review</button>
            {showReviewForm&&(
              <div style={{background:"white",border:`1px solid ${meta.color}33`,borderRadius:16,padding:18,marginBottom:16}}>
                <input value={reviewForm.name} onChange={e=>setReviewForm({...reviewForm,name:e.target.value})} placeholder="Your name" style={{width:"100%",padding:"9px 14px",border:"1px solid #e5e5e5",borderRadius:10,fontSize:13,outline:"none",marginBottom:10}}/>
                <div style={{display:"flex",gap:6,marginBottom:10}}>
                  {[1,2,3,4,5].map(r=>(
                    <button key={r} onClick={()=>setReviewForm({...reviewForm,rating:r})} style={{fontSize:22,background:"none",border:"none",cursor:"pointer",color:r<=reviewForm.rating?"#F59E0B":"#e5e5e5"}}>★</button>
                  ))}
                </div>
                <textarea value={reviewForm.text} onChange={e=>setReviewForm({...reviewForm,text:e.target.value})} placeholder="Share your experience..." style={{width:"100%",padding:"9px 14px",border:"1px solid #e5e5e5",borderRadius:10,fontSize:13,outline:"none",height:72,resize:"none",marginBottom:10}}/>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={submitReview} style={{background:meta.color,color:"white",border:"none",padding:"9px 22px",borderRadius:50,fontSize:12,fontWeight:700,cursor:"pointer"}}>Submit</button>
                  <button onClick={()=>setShowReviewForm(false)} style={{background:"white",color:"#666",border:"1px solid #e5e5e5",padding:"9px 18px",borderRadius:50,fontSize:12,cursor:"pointer"}}>Cancel</button>
                </div>
              </div>
            )}
            {allReviews.map((rv,i)=>(
              <div key={rv.id||i} style={{padding:"14px 0",borderBottom:"1px solid #f5f5f5"}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:5,flexWrap:"wrap",gap:6}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:30,height:30,borderRadius:"50%",background:meta.color,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:700,fontSize:12,flexShrink:0}}>{(rv.name||"?")[0].toUpperCase()}</div>
                    <span style={{fontWeight:700,fontSize:13}}>{rv.name}</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{color:"#F59E0B",fontSize:12}}>{"★".repeat(rv.rating||rv.r||5)}</span>
                    <span style={{fontSize:11,color:"#999"}}>{rv.date}</span>
                  </div>
                </div>
                <p style={{fontSize:13,color:"#666",lineHeight:1.6}}>{rv.text||rv.t}</p>
              </div>
            ))}
          </div>
        )}

        {tab==="qa"&&(
          <div style={{maxWidth:680}}>
            <div style={{ background:"#fafafa", borderRadius:14, padding:16, marginBottom:18 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#1a1a2e", marginBottom:8 }}>Have a question about this product?</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <input value={newQuestion} onChange={e=>setNewQuestion(e.target.value)} placeholder="Ask something, e.g. Does this run true to size?"
                  style={{ flex:1, minWidth:200, padding:"9px 14px", border:"1px solid #e5e5e5", borderRadius:10, fontSize:13, outline:"none" }}
                  onKeyDown={e=>{ if(e.key==="Enter" && newQuestion.trim()){ askQuestion(p.id, newQuestion, user?.name); setNewQuestion(""); } }}/>
                <button onClick={()=>{ if(newQuestion.trim()){ askQuestion(p.id, newQuestion, user?.name); setNewQuestion(""); } }}
                  style={{ background:meta.color, color:"white", border:"none", padding:"9px 20px", borderRadius:10, fontSize:12, fontWeight:700, cursor:"pointer" }}>Ask</button>
              </div>
            </div>

            {(productQA[p.id]||[]).length===0 && <p style={{ fontSize:13, color:"#999" }}>Koi sawal nahi hai abhi — pehla sawal poocho!</p>}

            {(productQA[p.id]||[]).map(q=>(
              <div key={q.id} style={{ padding:"14px 0", borderBottom:"1px solid #f5f5f5" }}>
                <div style={{ display:"flex", gap:8, marginBottom:6 }}>
                  <span style={{ fontSize:13 }}>❓</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#1a1a2e" }}>{q.question}</div>
                    <div style={{ fontSize:10, color:"#999", marginTop:2 }}>{q.askedBy} · {q.date}</div>
                  </div>
                </div>
                {q.answer ? (
                  <div style={{ display:"flex", gap:8, marginLeft:20 }}>
                    <span style={{ fontSize:13 }}>💬</span>
                    <div>
                      <div style={{ fontSize:13, color:"#666" }}>{q.answer}</div>
                      <div style={{ fontSize:10, color:"#999", marginTop:2 }}>Matchwell Team · {q.answerDate}</div>
                    </div>
                  </div>
                ) : admin ? (
                  <div style={{ marginLeft:20, display:"flex", gap:8, marginTop:6 }}>
                    <input value={answerDrafts[q.id]||""} onChange={e=>setAnswerDrafts({...answerDrafts,[q.id]:e.target.value})} placeholder="Type an answer..."
                      style={{ flex:1, padding:"7px 12px", border:"1px solid #e5e5e5", borderRadius:8, fontSize:12, outline:"none" }}/>
                    <button onClick={()=>{ answerQuestion(p.id, q.id, answerDrafts[q.id]||""); setAnswerDrafts({...answerDrafts,[q.id]:""}); }}
                      style={{ background:"#1a1a2e", color:"white", border:"none", padding:"7px 16px", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer" }}>Post</button>
                  </div>
                ) : (
                  <div style={{ marginLeft:20, fontSize:11, color:"#ccc", fontStyle:"italic" }}>Awaiting answer from Matchwell...</div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab==="ship"&&(
          <div style={{maxWidth:480}}>
            {(()=>{
              const priceText = freeShipThreshold>0 ? `Free above ₹${freeShipThreshold.toLocaleString("en-IN")}, else ₹${shippingFee}` : "Free on all orders";
              return [["Chh. Sambhajinagar (local)","3-4 days",priceText],["Maharashtra","5-7 days",priceText],["Rest of India","6-9 days",priceText]];
            })().map(([t,d,pr])=>(
              <div key={t} style={{display:"flex",justifyContent:"space-between",padding:"14px 0",borderBottom:"1px solid #f5f5f5",flexWrap:"wrap",gap:8}}>
                <div><div style={{fontWeight:700,fontSize:13,marginBottom:2}}>{t}</div><div style={{fontSize:12,color:"#999"}}>{d}</div></div>
                <div style={{fontWeight:700,color:meta.color,fontSize:13}}>{pr}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Related */}
      {related.length>0&&(
        <div style={{background:"#fafafa",padding:"32px clamp(14px,4vw,40px)"}}>
          <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(18px,3vw,28px)",fontWeight:900,color:"#1a1a2e",marginBottom:20}}>You May Also <em style={{fontStyle:"italic",color:"#EC4899"}}>Like</em></h3>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(clamp(140px,22vw,200px),1fr))",gap:"clamp(10px,2vw,16px)"}}>
            {related.map(rp=><ProductCard key={rp.id} p={rp} onNav={onNav} compact/>)}
          </div>
        </div>
      )}
      <Footer onNav={onNav}/>
    </div>
  );
}
